import React from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TwitterIcon from '@mui/icons-material/Twitter';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CallIcon from '@mui/icons-material/Call';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PlaceIcon from '@mui/icons-material/Place';
import ScheduleIcon from '@mui/icons-material/Schedule';

const GOLD = '#d4af37';
const DARK = '#060503';

const navLinks = [
  { label: 'About', to: '/about' },
  { label: 'Deities', to: '/about/deities' },
  { label: 'History', to: '/about/history' },
  { label: 'Festivals', to: '/about/festivals' },
  { label: 'Poojas', to: '/poojas/booking' },
  { label: 'Vazhipads', to: '/poojas/vazhipad' },
  { label: 'Donate', to: '/donate/online' },
  { label: 'Annadanam', to: '/donate/annadanam' },
  { label: 'Gallery', to: '/gallery' },
  { label: 'Nearby', to: '/nearby/temples' },
  { label: 'Contact', to: '/contact' },
];

const socials = [
  { I: FacebookIcon, h: 'https://facebook.com', l: 'Fb' },
  { I: InstagramIcon, h: 'https://instagram.com', l: 'Ig' },
  { I: YouTubeIcon, h: 'https://www.youtube.com/@srikottakovilakamtemple', l: 'Yt' },
  { I: TwitterIcon, h: 'https://twitter.com', l: 'Tw' },
];

export default function Footer() {
  return (
    <Box component="footer" sx={{ bgcolor: DARK, position: 'relative', overflow: 'hidden' }}>

      {/* ═══ SECTION 1 — Giant CTA with bold typography ═══ */}
      <Box sx={{
        position: 'relative',
        py: { xs: 2.5, md: 3 },
        overflow: 'hidden',
      }}>
        {/* Background watermark text */}
        <Typography sx={{
          position: 'absolute',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: { xs: '2.2rem', sm: '3.5rem', md: '5rem' },
          fontWeight: 900,
          color: 'rgba(255,255,255,0.012)',
          whiteSpace: 'nowrap',
          userSelect: 'none',
          letterSpacing: -4,
          lineHeight: 1,
        }}>
          MUCHUKUNNU
        </Typography>

        {/* Ambient glow */}
        <Box sx={{
          position: 'absolute',
          top: '30%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 500, height: 500, borderRadius: '50%',
          background: `radial-gradient(circle, ${GOLD}06 0%, transparent 60%)`,
          pointerEvents: 'none',
        }} />

        <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 3, md: 6 }, textAlign: 'center', position: 'relative', zIndex: 1 }}>
          {/* Logo */}
          <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ position: 'relative' }}>
              <Box sx={{
                position: 'absolute', inset: -5, borderRadius: '50%',
                border: `1px solid ${GOLD}15`,
                animation: 'ftSpin 10s linear infinite',
                '@keyframes ftSpin': { to: { transform: 'rotate(360deg)' } },
                '&::after': {
                  content: '""', position: 'absolute', top: -2, left: '50%',
                  transform: 'translateX(-50%)',
                  width: 3, height: 3, borderRadius: '50%',
                  bgcolor: GOLD, boxShadow: `0 0 6px ${GOLD}`,
                },
              }} />
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/assets/header_god_image.png`}
                alt="Temple"
                sx={{
                  width: 40, height: 40, borderRadius: '50%', objectFit: 'cover',
                  border: `2px solid ${GOLD}40`,
                  boxShadow: `0 0 20px ${GOLD}15`,
                }}
              />
            </Box>
          </Box>

          <Typography sx={{
            fontWeight: 900,
            fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.9rem' },
            lineHeight: 1.15,
            color: '#fff',
            mb: 1,
            letterSpacing: '-0.02em',
          }}>
            Your support keeps the
            <Box component="span" sx={{
              display: 'inline',
              background: `linear-gradient(135deg, ${GOLD} 0%, #e5c158 50%, ${GOLD} 100%)`,
              backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}> divine flame </Box>
            alive
          </Typography>

          <Typography sx={{
            fontSize: { xs: '0.78rem', md: '0.85rem' },
            color: 'rgba(255,255,255,0.4)',
            mb: 2, maxWidth: 460, mx: 'auto', lineHeight: 1.5,
          }}>
            Every contribution nurtures daily poojas, temple upkeep, and community programs.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              component={RouterLink} to="/donate/online" variant="contained"
              startIcon={<FavoriteIcon sx={{ fontSize: '14px !important' }} />}
              sx={{
                bgcolor: GOLD, color: DARK,
                fontWeight: 800, px: 3, py: 0.8, fontSize: '0.82rem',
                borderRadius: 12, letterSpacing: 0.5,
                boxShadow: `0 8px 32px ${GOLD}30`,
                transition: 'all 300ms cubic-bezier(0.4,0,0.2,1)',
                '&:hover': {
                  bgcolor: '#e5c158', transform: 'translateY(-3px) scale(1.02)',
                  boxShadow: `0 14px 44px ${GOLD}45`,
                },
              }}
            >
              Donate Now
            </Button>
            <Button
              component={RouterLink} to="/poojas/booking" variant="outlined"
              sx={{
                color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.12)',
                fontWeight: 700, px: 3, py: 0.8, fontSize: '0.82rem',
                borderRadius: 12, letterSpacing: 0.5,
                transition: 'all 300ms cubic-bezier(0.4,0,0.2,1)',
                '&:hover': {
                  borderColor: `${GOLD}60`, color: GOLD,
                  transform: 'translateY(-3px)',
                  bgcolor: `${GOLD}05`,
                },
              }}
            >
              Book a Pooja
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ═══ SECTION 2 — Horizontal band links ═══ */}
      <Box sx={{
        borderTop: `1px solid rgba(255,255,255,0.04)`,
        borderBottom: `1px solid rgba(255,255,255,0.04)`,
        py: 0,
        overflow: 'hidden',
      }}>
        <Box sx={{
          display: 'flex', alignItems: 'center',
          gap: 0, flexWrap: 'wrap',
          justifyContent: 'center',
        }}>
          {navLinks.map((link, i) => (
            <Box
              key={link.label}
              component={RouterLink} to={link.to}
              sx={{
                position: 'relative',
                color: 'rgba(255,255,255,0.4)',
                textDecoration: 'none',
                fontSize: { xs: '0.78rem', md: '0.88rem' },
                fontWeight: 600,
                py: { xs: 1.5, md: 1.2 },
                px: { xs: 1.5, md: 2 },
                letterSpacing: 0.3,
                transition: 'all 200ms ease',
                display: 'flex', alignItems: 'center', gap: 0.5,
                borderRight: i < navLinks.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                '&:hover': {
                  color: GOLD,
                  bgcolor: `${GOLD}06`,
                },
                '&:hover .linkArrow': { opacity: 1, transform: 'translate(0, 0)' },
              }}
            >
              {link.label}
              <NorthEastIcon className="linkArrow" sx={{
                fontSize: 11, opacity: 0,
                transform: 'translate(-4px, 4px)',
                transition: 'all 200ms ease',
              }} />
            </Box>
          ))}
        </Box>
      </Box>

      {/* ═══ SECTION 3 — Info + Socials bottom grid ═══ */}
      <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 3, md: 6 }, py: { xs: 2, md: 2.5 } }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 2.5, md: 5 },
          mb: 2,
        }}>
          {/* Left — Brand + description */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{
                width: 10, height: 10, borderRadius: '50%',
                bgcolor: GOLD, boxShadow: `0 0 12px ${GOLD}50`,
              }} />
              <Typography sx={{
                fontWeight: 800, fontSize: { xs: '1rem', md: '1.2rem' },
                background: `linear-gradient(135deg, #fff 30%, ${GOLD} 100%)`,
                backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                Muchukunnu Sri Kotta-Kovilakam
              </Typography>
            </Box>
            <Typography sx={{
              fontSize: '0.88rem', color: 'rgba(255,255,255,0.35)',
              lineHeight: 1.6, maxWidth: 420, mb: 1.5,
            }}>
              A sacred kshethram preserving ancient traditions, serving devotion through daily rituals, grand festivals, and community welfare since generations.
            </Typography>

            {/* Social — horizontal with labels */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {socials.map((s) => (
                <IconButton
                  key={s.l}
                  component="a" href={s.h} target="_blank" rel="noopener noreferrer"
                  sx={{
                    width: 40, height: 40, borderRadius: 1.5,
                    bgcolor: 'rgba(255,255,255,0.025)',
                    border: '1px solid rgba(255,255,255,0.05)',
                    color: 'rgba(255,255,255,0.35)',
                    transition: 'all 250ms ease',
                    '&:hover': {
                      bgcolor: GOLD, color: DARK,
                      transform: 'translateY(-3px)',
                      boxShadow: `0 6px 20px ${GOLD}30`,
                    },
                  }}
                >
                  <s.I sx={{ fontSize: 18 }} />
                </IconButton>
              ))}
            </Box>
          </Box>

          {/* Right — Contact info as minimal rows */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <Typography sx={{
              fontSize: '0.68rem', fontWeight: 700, color: GOLD,
              letterSpacing: 3, textTransform: 'uppercase', mb: 1.5,
            }}>
              Get in Touch
            </Typography>
            {[
              { icon: <PlaceIcon sx={{ fontSize: 16, color: GOLD }} />, text: 'Muchukunnu Akalappuzha Road, Moodadi, Kerala 673307', href: undefined as string | undefined },
              { icon: <CallIcon sx={{ fontSize: 16, color: GOLD }} />, text: '0496 2690751', href: 'tel:04962690751' },
              { icon: <MailOutlineIcon sx={{ fontSize: 16, color: GOLD }} />, text: 'info@kottakovilakam.com', href: 'mailto:info@kottakovilakam.com' },
              { icon: <ScheduleIcon sx={{ fontSize: 16, color: GOLD }} />, text: 'Kottayil Temple: 6:00 AM – 11:00 AM', href: undefined as string | undefined },
              { icon: <ScheduleIcon sx={{ fontSize: 16, color: GOLD }} />, text: 'Kovilakam: 5:30 AM – 8:30 AM, 5:30 PM – 7:30 PM', href: undefined as string | undefined },
            ].map((item, i) => (
              <Box
                key={i}
                component={item.href ? 'a' : 'div'}
                href={item.href}
                sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                py: 1,
                borderBottom: i < 4 ? '1px solid rgba(255,255,255,0.03)' : 'none',
                transition: 'all 200ms ease',
                textDecoration: 'none',
                cursor: item.href ? 'pointer' : 'default',
                '&:hover': { pl: 0.5, ...(item.href && { '& .footer-contact-text': { color: GOLD } }) },
              }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: 1,
                  bgcolor: `${GOLD}08`, border: `1px solid ${GOLD}12`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {item.icon}
                </Box>
                <Typography className="footer-contact-text" sx={{
                  fontSize: '0.88rem', color: 'rgba(255,255,255,0.55)',
                  fontWeight: 500, overflowWrap: 'break-word', wordBreak: 'break-word',
                  transition: 'color 200ms ease',
                }}>
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Bottom copyright ── */}
        <Box sx={{
          pt: 2,
          borderTop: '1px solid rgba(255,255,255,0.04)',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
        }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.2)', fontWeight: 500 }}>
            © {new Date().getFullYear()} Sri Kotta-Kovilakam Kshethram · Muchukunnu
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 1, bgcolor: 'rgba(255,255,255,0.08)' }} />
              <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.15)', fontWeight: 500, letterSpacing: 1 }}>
                PRESERVING TRADITION SINCE GENERATIONS
              </Typography>
              <Box sx={{ width: 20, height: 1, bgcolor: 'rgba(255,255,255,0.08)' }} />
            </Box>
            <Typography
              component={RouterLink}
              to="/privacy-policy"
              sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                color: 'rgba(255,255,255,0.45)',
                textDecoration: 'none',
                transition: 'color 200ms ease',
                '&:hover': { color: GOLD },
              }}
            >
              Privacy Policy
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Bottom gold line */}
      <Box sx={{
        height: 2,
        background: `linear-gradient(90deg, transparent, ${GOLD}50 30%, ${GOLD} 50%, ${GOLD}50 70%, transparent)`,
      }} />
    </Box>
  );
}
